import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';

import { IMarket, IOutcome, IOutcomeDetails, ISportEvent } from '@frontend/oxygen/shared-utils';
import * as _ from 'underscore';

import { BetslipBetDataUtils } from '../../../betslip/models/betslip-bet-data.utils';
import { EnvironmentConfig } from '../../../client-config/index';
import { BetslipSelectionsDataService } from '../../../core/services/betslipSelectionsData/betslip-selections-data';
import { CommandService } from '../../../core/services/communication/command/command.service';
import { PubSubService } from '../../../core/services/communication/pubsub/pubsub.service';
import { GtmService } from '../../../core/services/gtm/gtm.service';
import { GtmTrackingService } from '../../../core/services/gtmTracking/gtm-tracking.service';
import { IGtmBetPosition, IGtmOrigin } from '../../../core/services/gtmTracking/models/gtm-origin.model';
import { LocaleService } from '../../../core/services/locale/locale.service';
import { SportEventHelperService } from '../../../core/services/sportEventHelper/sport-event-helper.service';
import { SessionStorageService } from '../../../core/services/storage/session-storage.service';
import { StorageService } from '../../../core/services/storage/storage.service';
import { UserService } from '../../../core/services/user/user.service';
import { countries } from '../../../lazy-modules/nextRaces/nextRacesConstant/next-races.constant';
import { RoutingState } from '../../services/routingState/routing-state.service';

interface IBetData {
    [key: string]: string | boolean | number;
}
interface IGTMData {
    categoryID: string;
    typeID: string;
    eventID: string;
    selectionID: string;
    betData?: IBetData;
    tracking?: any;
    [key: string]: string | IBetData | IGtmOrigin | any;
}

@Component({
    selector: 'price-odds-button',
    templateUrl: './price-odds-button.component.html',
    standalone: false,
})
export class PriceOddsButtonComponent implements OnInit, OnDestroy {
    @Input() event: ISportEvent;
    @Input() market: IMarket;
    @Input() outcome: IOutcome;
    @Input() goToBetslip?: boolean;
    @Input() cssClass: string;
    @Input() head: string;
    @Input() isShowHistoricPrices: boolean = true;
    @Input() hasWasLabel: boolean = false;
    @Input() gtmModuleTitle?: string;
    @Input() gtmPlacedLocation?: string;
    @Input() nonRunner: boolean;
    @Input() handicapVal?: string;
    @Input() isRacing?: boolean;
    @Input() sbPosition?: number;
    @Input() correctName?: string;
    @Input() overUnderTag?: string;
    @Input() eventQuickSwitch: boolean;
    @Input() forYouGtmObj: IGtmBetPosition;
    @Input() isForYouBet: boolean;
    @Input() selectedFilter: string;
    @Input() showFilter: boolean;
    @Input() NrmLocation: string;
    @Input() bybWGtm: IGtmBetPosition;
    @Input() isPb?: boolean;
    @Input() cardId?: string;
    @Input() positionGtm?: IGtmBetPosition;

    oddsPrice: string;
    showHandicapValue: boolean = false;
    showSuspendValue: boolean = false;
    showCorrectScore: boolean = false;
    inplayPage: string = 'in-play/football';
    private readonly environment = inject(EnvironmentConfig).configuration;
    private env = this.environment;

    constructor(
        private userService: UserService,
        private gtmService: GtmService,
        private pubsubService: PubSubService,
        private commandService: CommandService,
        private betSlipSelectionsData: BetslipSelectionsDataService,
        private routingState: RoutingState,
        private gtmTrackingService: GtmTrackingService,
        private sportEventHelperService: SportEventHelperService,
        protected locale: LocaleService,
        protected sessionStorage: SessionStorageService,
        protected storageService: StorageService,
    ) {}

    ngOnInit(): void {
        if (this.handicapVal) {
            this.showHandicapValue = true;
            this.handicapVal = this.handicapVal.replace(/,/g, '');
        }
        if (this.correctName) {
            this.showCorrectScore = true;
        }

        this.pubsubService.subscribe(`priceOddsButton_${this.outcome.id}`, `SELECTION_PRICE_UPDATE_${this.outcome.id}`, (price: any) => {
            if (this.outcome.prices && this.outcome.prices[0]) {
                this.outcome.prices[0].priceDen = price.priceDen;
                this.outcome.prices[0].priceNum = price.priceNum;
            }
        });
    }

    ngOnDestroy(): void {
        this.outcome && this.pubsubService.unsubscribe(`priceOddsButton_${this.outcome.id}`);
    }

    showOddsPriceValue(showVal: boolean) {
        this.showSuspendValue = showVal;
    }

    /**
     *
     * Common Handler for button click
     *
     * @param {object} $event
     */
    onPriceOddsButtonClick($event: Event): void {
        $event.stopPropagation();

        this.commandService.executeAsync(this.commandService.API.IS_ADDTOBETSLIP_IN_PROCESS).then((inProgress: boolean) => {
            if (!inProgress) {
                this.pubsubService.publishSync(this.pubsubService.API.CLOSE_QUICKBET_PARTIAL_OVERLAY, this.outcome.id);
                this.addToBetSlip($event);
            }
        });
    }

    /**
     * Adding to BetSlip.
     * Placing bet for 'SP' button, we send priceNum, priceDen as undefined.
     * @private
     * @param {object} event
     */
    private addToBetSlip(event: Event): void {
        if (this.selectedFilter && this.showFilter) {
            this.gtmService.setSelectedFilter(this.selectedFilter);
            this.gtmService.setSelData(this.selectedFilter, this.event.id);
        }
        const segment: string = this.routingState.getCurrentSegment();
        this.outcome.prices = this.outcome.prices || [];
        const positionObj = this.isForYouBet ? this.forYouGtmObj : this.bybWGtm ? this.bybWGtm : this.positionGtm;
        const tracking = this.gtmTrackingService.detectTracking(
            this.gtmModuleTitle ?? '',
            segment,
            this.event,
            this.market,
            this.gtmPlacedLocation,
            positionObj,
        );
        if (this.eventQuickSwitch && tracking && tracking.location) {
            tracking.location = 'events switcher-' + tracking.location.toLowerCase();
        }
        const priceType = this.outcome.prices && this.outcome.prices[0] ? this.outcome.prices[0].priceType : 'SP';
        const price = _.extend({}, this.outcome.prices[0], priceType && { priceType });
        const isRacing: boolean = this.event.categoryId === '19' || this.event.categoryId === '21';

        if (isRacing) {
            price.priceType = this.getCorrectPriceType();
            if (
                this.NrmLocation &&
                this.event?.categoryId === '21' &&
                this.outcome &&
                !this.betSlipSelectionsData.getSelectionsByOutcomeId(this.outcome.id).length
            ) {
                if (this.event.drilldownTagNames?.includes('EVFLAG_IHR') && this.event.eventIsLive) {
                    this.pubsubService.publish(this.pubsubService.API.FLAG_TRACK_ADD, 'NrmBIR' + this.NrmLocation);
                    this.pubsubService.publish(this.pubsubService.API.FLAG_TRACK_ADD, 'NrmBIR');
                }
                this.pubsubService.publish(this.pubsubService.API.FLAG_TRACK_ADD, 'Nrm' + this.NrmLocation);
                this.pubsubService.publish(this.pubsubService.API.FLAG_TRACK_ADD, 'Nrm');
            }
        }

        const handicap = this.market.rawHandicapValue && {
            type: this.outcome.outcomeMeaningMajorCode,
            raw: this.outcome.prices[0].handicapValueDec.replace(/,/g, ''),
        };

        const isBuildYourBet = this.isByB();

        const GTMObject: IGTMData = {
            categoryID: this.event && String(this.event.categoryId),
            typeID: this.event && String(this.event.typeId),
            eventID: this.event && String(this.event.id),
            selectionID: this.outcome && String(this.outcome.id),
        };
        const isIPSort = this.routingState.getCurrentUrl()?.includes('in-play/football') && this.event.isLDWidget;
        this.gtmService.isStats = this.event.isStatsAvailable ?? false;
        if (tracking) {
            const activePill = this.storageService.get('activepill');
            const activeTab = this.storageService.get('activetab');
            if (this.gtmTrackingService.gtmObject.module === 'edp') {
                if (activePill && tracking.location !== 'BET BUILDER') {
                    tracking.location = 'markets-' + activePill.toLowerCase();
                } else if (activeTab && activeTab.label == 'Bet Builder') {
                    tracking.location = 'BET BUILDER';
                } else if (activeTab && activeTab.label && tracking.location !== 'BET BUILDER') {
                    tracking.location = 'markets-' + activeTab.label.toLowerCase();
                } else {
                    tracking.location === 'Football' ? this.gtmTrackingService.setLocation('BET BUILDER', 'location') : 'BET BUILDER';
                }
            }
            GTMObject['tracking'] = tracking;
            GTMObject['betData'] = {
                name: this.event.originalName || this.event.name,
                category: String(this.event.categoryId),
                variant: String(this.event.typeId),
                brand: this.market.marketName || this.market.name,
                dimension60: String(this.event.id),
                dimension61: this.outcome.id,
                dimension62: this.event.eventIsLive ? 1 : 0,
                dimension63: isBuildYourBet ? 1 : 0,
                dimension64: tracking.location ? tracking.location : 'LIVE NOW',
                dimension65: tracking.module ? tracking.module : 'event card',
                dimension177: this.market.isSCAvailable ? 'show' : 'No show',
                dimension180:
                    this.isForYouBet && this.forYouGtmObj
                        ? (this.forYouGtmObj?.personalised ?? '')
                        : tracking.module === 'next races' && this.event.categoryId == '39'
                          ? 'virtual'
                          : 'normal',
            };
            // for player bets
            if (this.isPb) {
                const market = this.event.markets?.find((eventMarket: IMarket) => this.outcome.marketId === eventMarket.id);
                if (market?.name && GTMObject['betData']) GTMObject['betData'].brand = market.name;
            }

            if (tracking.module === 'next events' && GTMObject['betData']) {
                GTMObject['betData'].dimension180 = 'virtual_hub';
                delete GTMObject['betData'].dimension177;
            }
            if (tracking.module && tracking.module.indexOf('popBetsPPHome') !== -1 && GTMObject['betData']) {
                GTMObject['betData'].dimension65 = tracking.module.replace('popBetsPPHome', '');
                GTMObject['betData'].dimension180 = 'Popular Pre-Play';
            }
            if (tracking.module === 'byb widget' && GTMObject['betData'] && positionObj) {
                GTMObject['betData'].dimension180 = positionObj?.position ?? '';
            }
            if (tracking.module && tracking.module.includes('popularAccasWidgetModule') && GTMObject['betData'] && positionObj) {
                GTMObject['betData'].dimension65 = tracking.module.replace('popularAccasWidgetModule', '');
                GTMObject['betData'].dimension180 = positionObj.position ?? '';
            }
            if (this.selectedFilter && this.showFilter) {
                this.gtmService.setSelectedFilter(this.selectedFilter);
                this.gtmService.setSelData(this.selectedFilter, this.event.id);
            }
            if (tracking.module == 'next races' && this.selectedFilter && this.showFilter && GTMObject['betData'] && countries[this.selectedFilter]) {
                GTMObject['betData']['dimension64'] = countries[this.selectedFilter].toLocaleLowerCase();
            }
            if (this.sbPosition && GTMObject['betData']) {
                // on surfacebet odds click provides the item position
                Object.assign(GTMObject['betData'], { dimension94: this.sbPosition });
            }
            if (this.gtmService.isStats && isIPSort && GTMObject['betData']) {
                GTMObject['betData']['dimension64'] = tracking.module || '';
                GTMObject['betData']['dimension65'] = 'event card';
            }
        }

        const details: Partial<IOutcomeDetails> | undefined = BetslipBetDataUtils.outcomeDetails(this.event, this.market, this.outcome);

        const addToBetSlipObject = {
            eventIsLive: this.event.eventIsLive, // solution for indicate in-play event
            outcomes: [this.outcome],
            typeName: this.event.typeName,
            price,
            handicap,
            goToBetslip: false,
            modifiedPrice: this.outcome.modifiedPrice,
            eventId: this.event.id,
            marketId: this.market.id || this.outcome.marketId,
            bEid: this.event.bwinId,
            bMid: this.market.bwinId || this.outcome.mrktBwinId,
            bSid: this.outcome.bwinId,
            bybAvailableEvent: this.event.bybAvailableEvent,
            bybAvailableMarket: this.market.bybAvailableMarket || this.outcome.bybAvailableMarket,
            bybInplayEligible: this.event.bybInplayEligible && this.market.bybInplayEligible,
            categoryId: GTMObject.categoryID,
            isOutright: this.sportEventHelperService.isOutrightEvent(this.event),
            isSpecial: this.sportEventHelperService.isSpecialEvent(this.event, true),
            isPopularBet: this.event.isPopularBet,
            isPlayerStats: this.isPb,
            isIPSort: isIPSort,
            GTMObject,
            details,
            eventName: this.event.originalName || this.event.name,
            cardId: this.cardId,
            isFeatured: !!this.event['@type'],
        };

        // send data customer places a bet and betslip opens
        if (addToBetSlipObject.goToBetslip && !this.userService.quickBetNotification) {
            this.gtmService.push('trackPageview', { virtualUrl: '/betslip-receipt' });
        }

        this.betSlipSelectionsData.addToBetslip([addToBetSlipObject], false);
    }

    private getCorrectPriceType(): string {
        return this.market.isLpAvailable && this.outcome.prices.length && !this.outcome.isFavourite ? 'LP' : 'SP';
    }

    private isByB(): boolean {
        return Boolean(
            this.event && this.env && this.env.BYB_CONFIG && String(this.env.BYB_CONFIG.HR_YC_EVENT_TYPE_ID) === String(this.event.typeId),
        );
    }
}
